import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { path, prop } from 'ramda';

import Overlay from '@core/js/components/common/Overlay';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { MenuItem } from '@interlayer/js/app/components/main/DefaultMenu/components/MenuItem/MenuItem';
import { InboxCounter } from '@interlayer/js/app/components/main/InboxCounter';
import { Skeleton } from '@interlayer/js/app/components/main/Skeleton';

import { IMenuItemConfig } from '@core/js/components/system/App/store/types';
import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import { useSwitchAccountMenu } from '@interlayer/js/app/components/main/DefaultHeader/components/HeaderMobile/hooks';
import { TYPES_BLOCK } from '@interlayer/js/app/components/main/DefaultMenu/components/MenuItem/constants';
import { MENU_CLASSES } from '@interlayer/js/app/components/main/DefaultMenu/constants';
import { SKELETON_TYPES } from '@interlayer/js/app/components/main/Skeleton/constants';
import { useInDesktop } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useMenu } from '@interlayer/js/app/hooks/menu/useMenu';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useClickOutSide } from '@interlayer/js/app/utils/common.util';
import { useOpenInbox } from '@interlayer/js/components/system/CommunicationWidgets/hooks/useOpenInbox';

import { TSideMenuRightProps } from './types';

import style from './SideMenuRight.scss';

const checkItemMenu = ({ cssClasses }: IMenuItemConfig): boolean =>
    [MENU_CLASSES.ONLY_FOR_LEFT, MENU_CLASSES.ONLY_FOR_TOP, MENU_CLASSES.NOT_FOR_RIGHT]
        .every(child => !cssClasses.includes(child));

export const SideMenuRight: React.FC<TSideMenuRightProps> = ({ isInDom, isVisible }) => {
    const filteredMenu = useMenu(checkItemMenu);
    const switchAccountMenu = useSwitchAccountMenu();
    const { pathname } = useLocation();
    const accountMenuRef = useRef<HTMLDivElement>(null);
    const isDesktop = useInDesktop();
    const firstName = useUserSelector(path(['userDetails', 'firstName']));
    const t = useTranslations('playtech.mobile.system.account-menu.');
    const isLoadingMenuItems = useAppSelector(prop('isLoadingMenuItems'));
    const showIcon = useAppSelector(path(['inboxWidget', 'showIcon']));

    const onOpenInbox = useOpenInbox();

    useClickOutSide(accountMenuRef, () => switchAccountMenu(false));

    useEffect(() => {
        switchAccountMenu(false);
    }, [pathname, switchAccountMenu]);

    const handleClickMailIcon = () => {
        onOpenInbox();
        switchAccountMenu(false);
    };

    if (!isInDom) {
        return (
            <div className={style.menuWrap} ref={accountMenuRef} />
        );
    }

    return (
        <>
            <div className={clsx(style.menuWrap, { [style.isOpened]: isVisible })} ref={accountMenuRef}>
                <div className={style.header}>
                    <SvgIcon
                        width="1.3rem"
                        height="1.3rem"
                        onClick={() => switchAccountMenu(false)}
                        className={style.close}
                        type="close-popup"
                    />

                    {showIcon && !isDesktop && (
                        <div onClick={handleClickMailIcon} className={style.inbox}>
                            <InboxCounter className={style.counter} type="inbox">
                                <SvgIcon type="mail" />
                            </InboxCounter>
                        </div>
                    )}
                </div>

                <div className={style.hello}>
                    {t('hi')},

                    {firstName}
                </div>

                <span className={clsx(style.divider, 'divider-bg')} />

                <div className={style.menu}>
                    {isLoadingMenuItems
                        ? (
                            <Skeleton configuration={[{ type: SKELETON_TYPES.menuItem, count: 10 }]} />
                        )
                        : filteredMenu.map((item) => (
                            <MenuItem
                                data={item}
                                key={item.url}
                                typeBlock={TYPES_BLOCK.right}
                            />
                        ))
                    }
                </div>
            </div>

            {isVisible && (
                <Overlay
                    className={style.overlay}
                    onClick={() => switchAccountMenu(false)}
                />
            )}
        </>
    );
};
